import {
  Button,
  FormControl,
  FormField,
  IconButton,
  UnsafeFieldAssertionPath,
} from 'platform/components';
import {Align, Box, HStack, Stack} from 'platform/foundation';

import {
  ArrayPath,
  FieldValues,
  Path,
  useFieldArray,
  useFormState,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import {any, nth} from 'ramda';
import {isNilOrEmpty, lengthLt} from 'ramda-adjunct';

import {EmailData} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {getByPathSafely, suffixTestId, TestIdProps} from 'shared/web';

import {isInvalidEmail} from '../../utils/isInvalidEmail';

type EmailListProps<TFieldValues extends FieldValues = FieldValues> = {
  control: FormControl<TFieldValues>;
  formApi: UseFormReturn<TFieldValues>;
  name: ArrayPath<TFieldValues>;
  isRequired?: boolean;
  isDisabled?: boolean;
} & TestIdProps;

export function EmailList<TFieldValues extends FieldValues = FieldValues>(
  props: EmailListProps<TFieldValues>
) {
  const {fields, remove, append} = useFieldArray({
    control: props.control,
    name: props.name,
  });

  const emptyEmail: EmailData = {
    type: null,
    email: '',
  };

  const emails = useWatch({
    control: props.formApi.control,
    name: props.name as Path<TFieldValues>,
  });

  // The whole form state: a field-array path is no field path for the `name` filter
  const {dirtyFields} = useFormState({control: props.formApi.control});

  const isAddAnotherDisabled = any((item: EmailData) => isNilOrEmpty(item.email))(emails);

  // A stored e-mail that the API refuses is marked before the first submit, so the user sees
  // that it must be fixed or removed.
  const getStoredEmailError = (index: number) => {
    const isDirty =
      getByPathSafely(dirtyFields, [...props.name.split('.'), index, 'email']) === true;

    return !isDirty && isInvalidEmail(nth<EmailData>(index, emails ?? [])?.email)
      ? i18n.t('general.validations.invalidEmail')
      : undefined;
  };

  return (
    <>
      {fields.map((field, index) => (
        <Stack key={field.id} direction={['column', 'row']} spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              type="email"
              isRequired={props.isRequired}
              isDisabled={props.isDisabled}
              name={
                `${props.name}.${index}.email` as UnsafeFieldAssertionPath<TFieldValues, string>
              }
              label={i18n.t('entity.email.labels.email')}
              errorMessage={getStoredEmailError(index)}
              data-testid={suffixTestId(`email-[${index}]`, props)}
            />
          </Box>
          <Box flex={1}>
            <HStack spacing={4} align="flex-end">
              <Box flex={1}>
                <FormField
                  control={props.control}
                  type="text"
                  isDisabled={props.isDisabled}
                  name={`${props.name}.${index}.type` as Path<TFieldValues>}
                  label={i18n.t('entity.email.labels.type')}
                  data-testid={suffixTestId(`emailType-[${index}]`, props)}
                />
              </Box>
              <IconButton
                icon="action/delete"
                isDisabled={lengthLt(2, fields)}
                severity="danger"
                onClick={() => remove(index)}
                data-testid={suffixTestId(`emailRemove-[${index}]`, props)}
              />
            </HStack>
          </Box>
        </Stack>
      ))}
      <Align left>
        <Button
          leftIcon="content/add_circle"
          title={i18n.t('general.actions.addAnother')}
          size="small"
          variant="link"
          isDisabled={isAddAnotherDisabled}
          onClick={() => append(emptyEmail as any)} // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
          data-testid={suffixTestId('emailAddAnother', props)}
        />
      </Align>
    </>
  );
}
